/**
 * hc-upsell-rows.js
 *
 * Replaces the upsell rendering that lived in hc-fixes.js.
 *
 * WHY THIS EXISTS
 * The size dropdown did not update the price: picking "400g" on All In One
 * left Rs. 10 on screen (the 32g price) while the button would have added the
 * 400g variant - the shopper sees one price and is charged another.
 *
 * The old change handler fetched /products/<handle>.js on every change and
 * updated the price from the response. On this storefront that request
 * regularly never completes, so the price simply never changed.
 *
 * FIX
 * Every variant's price is now baked onto its <option> as data attributes
 * when the row is built, from one catalogue load. Changing the dropdown reads
 * those attributes synchronously - no network call, nothing to fail.
 *
 * The pool is also built from the whole catalogue rather than only the 8
 * products the section renders, so the suggestions never run out. The
 * merchant's "Upsell collection" still comes first.
 */
(function () {
  'use strict';

  var VISIBLE = 4;

  function money(paise) {
    var amount = (paise || 0) / 100;
    var dec = Math.round(amount * 100) % 100 !== 0;
    return 'Rs. ' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: dec ? 2 : 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function esc(s) {
    var m = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return m[c]; });
  }

  var nativeFetch = window.fetch.bind(window);

  // ---------------------------------------------------------- catalogue ---

  var pool = [];          // every sellable product, full variant data
  var order = [];         // handles the merchant chose, in their order
  var loaded = false;
  var loading = null;

  function loadCatalogue() {
    if (loaded) return Promise.resolve();
    if (loading) return loading;

    loading = nativeFetch('/collections/all/products.json?limit=250')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        (data.products || []).forEach(function (p) {
          if (/build-your-own-hamper/.test(p.handle)) return;   // hidden pack product

          var vs = (p.variants || []).filter(function (v) { return v.available; });
          if (!vs.length) return;

          var img = p.images && p.images[0] ? p.images[0].src : '';
          if (img) img = img.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');

          pool.push({
            handle: p.handle,
            title: p.title,
            url: '/products/' + p.handle,
            image: img,
            ids: vs.map(function (v) { return String(v.id); }),
            variants: vs.map(function (v) {
              // /products.json gives price as a decimal STRING ("120.00")
              var price = Math.round(parseFloat(v.price) * 100);
              var cmp = v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0;
              return {
                id: String(v.id),
                title: v.title === 'Default Title' ? '' : v.title,
                price: price,
                compare: cmp > price ? cmp : 0
              };
            })
          });
        });
        loaded = true;
      })
      .catch(function () { /* leave pool empty; block stays hidden */ });

    return loading;
  }

  /** Read which products the section rendered, to preserve merchant ordering. */
  function readOrder(list) {
    Array.prototype.forEach.call(list.querySelectorAll('[data-hc-upsell-item]'), function (li) {
      var a = li.querySelector('.hc-upsell__title');
      var href = a && a.getAttribute('href');
      if (!href) return;
      var h = (href.split('/products/')[1] || '').split('?')[0];
      if (h && order.indexOf(h) === -1) order.push(h);
    });
  }

  function ranked() {
    return pool.slice().sort(function (a, b) {
      var ia = order.indexOf(a.handle), ib = order.indexOf(b.handle);
      if (ia === -1) ia = 9999;
      if (ib === -1) ib = 9999;
      return ia - ib;
    });
  }

  // -------------------------------------------------------------- markup ---

  function rowHTML(p) {
    var v0 = p.variants[0];
    var multi = p.variants.length > 1;

    var opts = p.variants.map(function (v) {
      return '<option value="' + esc(v.id) + '"' +
        ' data-price="' + v.price + '"' +
        ' data-compare="' + v.compare + '">' +
        esc(v.title || p.title) + '</option>';
    }).join('');

    return '<li class="hc-upsell__item hcx-row" data-hcx-item data-handle="' + esc(p.handle) + '">' +
      '<a class="hcx-row__img" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' +
        (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">'
                 : '<span class="hcx-row__ph"></span>') +
      '</a>' +
      '<div class="hcx-row__info">' +
        '<a class="hcx-row__title" href="' + esc(p.url) + '" title="' + esc(p.title) + '">' + esc(p.title) + '</a>' +
        '<span class="hcx-row__price">' +
          '<span class="hcx-row__now">' + money(v0.price) + '</span>' +
          (v0.compare ? '<s class="hcx-row__was">' + money(v0.compare) + '</s>' : '') +
        '</span>' +
        (multi
          ? '<select class="hcx-row__variant" data-hcx-variant aria-label="' + esc(p.title) + ' size">' + opts + '</select>'
          : '<input type="hidden" data-hcx-variant value="' + esc(v0.id) + '">') +
      '</div>' +
      '<button type="button" class="hcx-row__add" data-hcx-add aria-label="Add ' + esc(p.title) + ' to cart">Add</button>' +
    '</li>';
  }

  // ---------------------------------------------------------------- wire ---

  function init(root) {
    var list = root.querySelector('.hc-upsell__list');
    if (!list) return;

    readOrder(list);

    if (root.__hcxRows) { root.__hcxRows(); return; }
    root.__hcxRows = function () { render(); };

    list.classList.add('hcx-rail');

    var inCart = {};

    function eligible() {
      return ranked().filter(function (p) {
        return !p.ids.some(function (id) { return inCart[id]; });
      });
    }

    function render() {
      var items = eligible().slice(0, VISIBLE);
      root.hidden = items.length === 0;
      list.innerHTML = items.map(rowHTML).join('');
    }

    // Price follows the dropdown, read straight off the option. No fetch.
    list.addEventListener('change', function (e) {
      var sel = e.target.closest('select[data-hcx-variant]');
      if (!sel) return;

      var row = sel.closest('[data-hcx-item]');
      var opt = sel.options[sel.selectedIndex];
      if (!row || !opt) return;

      var price = parseInt(opt.getAttribute('data-price'), 10) || 0;
      var compare = parseInt(opt.getAttribute('data-compare'), 10) || 0;

      var nowEl = row.querySelector('.hcx-row__now');
      if (nowEl) nowEl.textContent = money(price);

      var wasEl = row.querySelector('.hcx-row__was');
      if (compare) {
        if (wasEl) {
          wasEl.textContent = money(compare);
        } else {
          var s = document.createElement('s');
          s.className = 'hcx-row__was';
          s.textContent = money(compare);
          row.querySelector('.hcx-row__price').appendChild(s);
        }
      } else if (wasEl) {
        wasEl.remove();
      }
    });

    list.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hcx-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      var row = btn.closest('[data-hcx-item]');
      var field = row.querySelector('[data-hcx-variant]');
      if (!field || !field.value) return;

      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding';
      row.classList.add('is-adding');

      nativeFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: field.value, quantity: 1 }] })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status) throw new Error('add failed');
          row.classList.add('is-leaving');
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return refreshInCart();
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = original;
          row.classList.remove('is-adding', 'is-leaving');
        });
    });

    function refreshInCart() {
      return nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          inCart = {};
          (cart.items || []).forEach(function (i) { inCart[String(i.variant_id)] = true; });
          render();
        })
        .catch(function () {});
    }

    root.__hcxSync = refreshInCart;

    loadCatalogue().then(refreshInCart);
  }

  function boot() {
    document.querySelectorAll('.hc-upsell').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  ['cart:refresh', 'cart:updated', 'hamper-builder:added'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      document.querySelectorAll('.hc-upsell').forEach(function (r) {
        if (r.__hcxSync) r.__hcxSync();
      });
    });
  });

  var t = null;
  new MutationObserver(function () {
    clearTimeout(t);
    t = setTimeout(boot, 300);
  }).observe(document.body, { childList: true, subtree: true });
})();
